import { Controller } from "@hotwired/stimulus";
import { onTopModalChange, topModal } from "../lib/modal_stack";

/** Un <turbo-frame> (Turbo ne publie pas ses types). */
type FrameElement = HTMLElement & { reload(): void };

/**
 * Un élément flottant de la page (le plateau des exports) qui monte dans la <dialog> modale du
 * dessus et redescend à sa place quand elle se ferme. À l'écran il ne bouge pas (fixed) ; mais
 * hors de la modale, même peint par-dessus, il serait inerte : une modale rend inerte tout ce
 * qui n'est pas elle, d'après le DOM et non l'ordre d'affichage.
 *
 * Monté sur la dialog même — son contenu peut rogner (overflow-hidden). Sa place d'origine est
 * marquée par un commentaire, reposé à chaque connexion hors modale (une navigation Turbo
 * déplace un élément permanent). Un déplacement reconnecte ce qu'il contient : ses turbo-frames
 * à `src` sont rechargés, un signal a pu tomber pendant le réabonnement de leur flux.
 */
export default class extends Controller<HTMLElement> {
  private home?: Comment;
  private stop?: () => void;

  connect(): void {
    if (!this.element.closest("dialog")) this.markHome();
    this.stop = onTopModalChange(() => this.place());
    this.place();
  }

  disconnect(): void {
    this.stop?.();
  }

  private markHome(): void {
    this.home?.remove();
    this.home = document.createComment(`${this.identifier}#${this.element.id}`);
    this.element.before(this.home);
  }

  private place(): void {
    const modal = topModal();
    if (modal) {
      if (this.element.parentElement === modal) return;
      modal.append(this.element);
    } else {
      if (!this.element.closest("dialog")) return;
      if (this.home?.isConnected) this.home.after(this.element);
      else document.body.append(this.element);
    }

    this.element.querySelectorAll<FrameElement>("turbo-frame[src]").forEach((frame) => frame.reload());
  }
}
